import { useEffect, useMemo, useRef, useState } from 'react';

import AddLocationModal, {
  LocationDraft,
} from '../../components/modals/AddLocationModal';
import { JobDraftV2, useOrderActionsV2, useOrderStateV2 } from '../../context';
import {
  PlacePrediction,
  getPlaceAddress,
  searchPlaces,
} from '../../utils/googlePlaces';
import {
  CreateLocationPayload,
  CreateShiftSupervisorPayload,
  LocationListItem,
  SavedSnippet,
  SavedSnippetFieldKind,
  ShiftSupervisor,
  SkillCategoryListItem,
  SkillListItem,
} from '../../api/types';
import { slugifySkillName } from './categoryMeta';
import {
  AddRow,
  CaretButton,
  CategoryCard,
  CategoryCircle,
  CategoryIconImg,
  CategoryLabel,
  CategoryRow,
  DetailsCard,
  DropdownFooter,
  DropdownItem,
  DropdownPanel,
  DropdownScroll,
  Field,
  FieldGroup,
  FieldHeading,
  FieldHint,
  InputShell,
  InstructionActions,
  LinkButton,
  NoContactDivider,
  NoContactRow,
  PageContent,
  PlusGlyph,
  PrimaryButton,
  ReadonlyInput,
  SaveAffordance,
  SaveButton,
  SecondaryButton,
  SectionGroup,
  SectionTitle,
  Select,
  SelectInput,
  SelectedBadge,
  SkillCard,
  SkillIconFallback,
  SkillIconImg,
  SkillIconWrap,
  SkillLabel,
  SkillRow,
  SupervisorOtherFields,
  TextArea,
  TextInput,
  ToggleKnob,
  ToggleLabel,
  ToggleRow,
  ToggleSwitch,
  TriggerSelect,
} from './styles';

type SnippetApplyHandler = (body: string) => void;

type JobTypeStepProps = {
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  skills: SkillListItem[];
  skillsLoading: boolean;
  skillsError: string | null;
  categories: SkillCategoryListItem[];
  onSkillSearchChange: (search: string) => void;
  locations: LocationListItem[];
  onCreateLocation: (
    payload: CreateLocationPayload,
  ) => Promise<LocationListItem | null>;
  supervisors: ShiftSupervisor[];
  onCreateSupervisor: (
    payload: CreateShiftSupervisorPayload,
  ) => Promise<ShiftSupervisor | null>;
  onBrowseSaved: (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    onApply: SnippetApplyHandler,
  ) => void;
  onSaveSnippet: (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    currentBody: string,
  ) => void;
  addressSnippets: SavedSnippet[];
  checkInSnippets: SavedSnippet[];
};

const CheckBadge = () => (
  <SelectedBadge>
    <svg
      viewBox="0 0 24 24"
      style={{ width: 24, height: 24, display: 'block' }}
    >
      <circle cx="12" cy="12" r="12" fill="#44A735" />
      <polyline
        points="7 12 10 15 17 9"
        fill="none"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SelectedBadge>
);

const ChevronDown = ({ size = 20 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1C1C1C"
    strokeWidth="2"
    style={{ width: size, height: size, flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SaveIcon = ({ stroke = '#979797' }: { stroke?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="1.5"
    style={{ width: 20, height: 20, flexShrink: 0 }}
  >
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const locationLabel = (location: LocationListItem) =>
  location.name ||
  location.full_address ||
  [location.address_line1, location.city, location.state]
    .filter(Boolean)
    .join(', ');

const supervisorLabel = (supervisor: ShiftSupervisor) =>
  supervisor.full_name || `Supervisor #${supervisor.id}`;

const JobTypeStep = ({
  jobs,
  onUpdateJobs,
  skills,
  skillsLoading,
  skillsError,
  categories,
  onSkillSearchChange,
  locations,
  onCreateLocation,
  supervisors,
  onCreateSupervisor,
  onBrowseSaved,
  onSaveSnippet,
  addressSnippets,
  checkInSnippets,
}: JobTypeStepProps) => {
  const job = jobs[0];
  const { verificationEnabled, verificationVisible } = useOrderStateV2();
  const { setVerificationEnabled } = useOrderActionsV2();

  const update = <K extends keyof JobDraftV2>(
    field: K,
    value: JobDraftV2[K],
  ) => {
    const nextJobs = [...jobs];
    nextJobs[0] = { ...nextJobs[0], [field]: value };
    onUpdateJobs(nextJobs);
  };

  // Skills are billing-account scoped; don't filter by search here — keep all loaded.
  useEffect(() => {
    onSkillSearchChange('');
  }, [onSkillSearchChange]);

  const selectedSkill = useMemo(
    () => skills.find((skill) => skill.id === job?.skill_id) ?? null,
    [skills, job?.skill_id],
  );

  // Default category derives from the selected skill; otherwise the first
  // category returned by the backend (alphabetical). Tracked by id so the
  // selection survives any rename or display-name churn.
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    () => selectedSkill?.category?.id ?? null,
  );

  useEffect(() => {
    if (selectedSkill?.category?.id) {
      setSelectedCategoryId(selectedSkill.category.id);
    }
  }, [selectedSkill?.category?.id]);

  useEffect(() => {
    if (selectedCategoryId === null && categories.length > 0) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  const skillsByCategoryId = useMemo(() => {
    const groups = new Map<number, SkillListItem[]>();
    skills.forEach((skill) => {
      const id = skill.category?.id;
      if (id == null) return;
      const list = groups.get(id) ?? [];
      list.push(skill);
      groups.set(id, list);
    });
    return groups;
  }, [skills]);

  const visibleSkills =
    selectedCategoryId !== null
      ? (skillsByCategoryId.get(selectedCategoryId) ?? [])
      : [];

  // Location dropdown
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>(
    [],
  );
  const [locationModalDraft, setLocationModalDraft] =
    useState<Partial<LocationDraft> | null>(null);
  const locationRef = useRef<HTMLDivElement | null>(null);
  const placeSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedLocation = useMemo(
    () => locations.find((location) => location.id === job?.address_id) ?? null,
    [locations, job?.address_id],
  );

  // Keep search text in sync with the selected location when one is picked.
  useEffect(() => {
    if (selectedLocation) {
      setLocationSearch(locationLabel(selectedLocation));
    } else {
      setLocationSearch('');
    }
  }, [selectedLocation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLocations = useMemo(() => {
    const term = locationSearch.trim().toLowerCase();
    if (
      !term ||
      (selectedLocation &&
        term === locationLabel(selectedLocation).toLowerCase())
    ) {
      return locations;
    }
    return locations.filter((location) =>
      [
        location.name,
        location.full_address,
        location.address_line1,
        location.city,
        location.state,
        location.zip,
      ]
        .filter(Boolean)
        .some((value) => (value as string).toLowerCase().includes(term)),
    );
  }, [locations, locationSearch, selectedLocation]);

  // Google Places search keyed off the location dropdown's input.
  useEffect(() => {
    if (placeSearchRef.current) {
      clearTimeout(placeSearchRef.current);
    }
    const term = locationSearch.trim();
    if (
      !term ||
      term.length < 3 ||
      (selectedLocation && term === locationLabel(selectedLocation))
    ) {
      setPlacePredictions([]);
      return;
    }
    placeSearchRef.current = setTimeout(() => {
      searchPlaces(term).then(setPlacePredictions);
    }, 300);
    return () => {
      if (placeSearchRef.current) clearTimeout(placeSearchRef.current);
    };
  }, [locationSearch, selectedLocation]);

  const handleSelectPlace = async (prediction: PlacePrediction) => {
    setLocationOpen(false);
    setLocationError(null);
    try {
      const address = await getPlaceAddress(prediction.place_id);
      setLocationModalDraft({
        address_line1: address?.address_line1 || prediction.main_text,
        city: address?.city ?? '',
        state: address?.state ?? '',
        zip: address?.zip ?? '',
      });
    } catch {
      setLocationModalDraft({ address_line1: prediction.description });
    }
    setLocationModalOpen(true);
  };

  // Supervisor dropdown
  const [supervisorOpen, setSupervisorOpen] = useState(false);
  const [supervisorOtherOpen, setSupervisorOtherOpen] = useState(false);
  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorPhone, setSupervisorPhone] = useState('');
  const [savingSupervisor, setSavingSupervisor] = useState(false);
  const [supervisorError, setSupervisorError] = useState<string | null>(null);
  const supervisorRef = useRef<HTMLDivElement | null>(null);

  const selectedSupervisor = useMemo(
    () =>
      supervisors.find((supervisor) => supervisor.id === job?.contact_id) ??
      null,
    [supervisors, job?.contact_id],
  );

  // Click-outside for both dropdowns
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        locationOpen &&
        locationRef.current &&
        !locationRef.current.contains(event.target as Node)
      ) {
        setLocationOpen(false);
      }
      if (
        supervisorOpen &&
        supervisorRef.current &&
        !supervisorRef.current.contains(event.target as Node)
      ) {
        setSupervisorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [locationOpen, supervisorOpen]);

  const handleSelectLocation = (location: LocationListItem) => {
    update('address_id', location.id);
    setLocationOpen(false);
  };

  const handleSaveLocation = async (draft: LocationDraft) => {
    setSavingLocation(true);
    setLocationError(null);
    try {
      const created = await onCreateLocation({
        name: draft.nickname.trim(),
        address_line1: draft.address_line1.trim(),
        address_line2: draft.address_line2.trim() || undefined,
        city: draft.city.trim(),
        state: draft.state.trim(),
        zip: draft.zip.trim(),
      });
      if (!created) {
        setLocationError('Could not save location');
        return;
      }
      const nextJobs = [...jobs];
      nextJobs[0] = {
        ...nextJobs[0],
        address_id: created.id,
        address_instructions: draft.how_to_get_there,
      };
      onUpdateJobs(nextJobs);
      setLocationModalOpen(false);
      setLocationModalDraft(null);
    } catch (error) {
      setLocationError((error as Error).message || 'Could not save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSelectSupervisor = (supervisor: ShiftSupervisor) => {
    update('contact_id', supervisor.id);
    setSupervisorOpen(false);
    setSupervisorOtherOpen(false);
  };

  const handleAddSupervisor = async () => {
    setSupervisorError(null);
    if (!supervisorName.trim() || !supervisorPhone.trim()) {
      setSupervisorError('Name and contact are required');
      return;
    }
    setSavingSupervisor(true);
    try {
      const created = await onCreateSupervisor({
        name: supervisorName.trim(),
        contact: supervisorPhone.trim(),
      });
      if (created) {
        update('contact_id', created.id);
      }
      setSupervisorName('');
      setSupervisorPhone('');
      setSupervisorOtherOpen(false);
    } catch (error) {
      setSupervisorError(
        (error as Error).message || 'Failed to add supervisor',
      );
    } finally {
      setSavingSupervisor(false);
    }
  };

  const showNoContactInput = !!selectedSupervisor && !selectedSupervisor.phone;

  const howToGetThere = job?.address_instructions || '';
  const checkIn = job?.contact_instructions || '';
  const shareContact = !!job?.share_contact_number;

  const matchesExistingSnippet = (body: string, snippets: SavedSnippet[]) => {
    const trimmed = body.trim();
    if (!trimmed) return false;
    return snippets.some((snippet) => snippet.body.trim() === trimmed);
  };

  const howToGetThereMatches = matchesExistingSnippet(
    howToGetThere,
    addressSnippets,
  );
  const checkInMatches = matchesExistingSnippet(checkIn, checkInSnippets);

  return (
    <PageContent>
      {/* Select job category */}
      <SectionGroup>
        <SectionTitle>Select job category</SectionTitle>
        <CategoryRow>
          {categories.map((cat) => {
            const isSelected = cat.id === selectedCategoryId;
            const label = cat.display_name_en || cat.name;
            return (
              <CategoryCard
                key={cat.id}
                selected={isSelected}
                onClick={() => setSelectedCategoryId(cat.id)}
              >
                <CategoryCircle>
                  {cat.image_url ? (
                    <CategoryIconImg src={cat.image_url} alt={label} />
                  ) : null}
                </CategoryCircle>
                <CategoryLabel selected={isSelected}>{label}</CategoryLabel>
                {isSelected ? <CheckBadge /> : null}
              </CategoryCard>
            );
          })}
        </CategoryRow>
      </SectionGroup>

      {/* Select job skill */}
      <SectionGroup>
        <SectionTitle>Select job skill</SectionTitle>
        {skillsError ? (
          <p style={{ color: '#C0392B', fontSize: 12, margin: 0 }}>
            {skillsError}
          </p>
        ) : null}
        <SkillRow>
          {visibleSkills.length === 0 && !skillsLoading ? (
            <p style={{ color: '#979797', fontSize: 12, margin: 0 }}>
              No skills available in this category for this billing account.
            </p>
          ) : (
            visibleSkills.map((skill) => {
              const isSelected = skill.id === job?.skill_id;
              const displayName = skill.display_name_en || skill.name;
              const slug = slugifySkillName(skill.name);
              return (
                <SkillCard
                  key={skill.id}
                  selected={isSelected}
                  onClick={() =>
                    onUpdateJobs(
                      jobs.map((j, i) =>
                        i === 0
                          ? {
                              ...j,
                              skill_id: skill.id,
                              uniform_id:
                                skill.default_uniform_id ??
                                j.uniform_id ??
                                null,
                            }
                          : j,
                      ),
                    )
                  }
                >
                  <SkillIconWrap>
                    {skill.image_url ? (
                      <SkillIconImg
                        src={skill.image_url}
                        alt={displayName}
                        onError={(event) => {
                          (
                            event.currentTarget as HTMLImageElement
                          ).style.display = 'none';
                        }}
                      />
                    ) : (
                      <SkillIconFallback
                        aria-label={displayName}
                        data-slug={slug}
                      />
                    )}
                  </SkillIconWrap>
                  <SkillLabel selected={isSelected}>{displayName}</SkillLabel>
                  {isSelected ? <CheckBadge /> : null}
                </SkillCard>
              );
            })
          )}
        </SkillRow>
      </SectionGroup>

      {/* Details card */}
      <DetailsCard>
        {/* Location group */}
        <FieldGroup>
          {/* Where is your job at? */}
          <Field>
            <FieldHeading>Where is your job at?</FieldHeading>
            <InputShell ref={locationRef}>
              <Select onClick={() => setLocationOpen(true)}>
                <SelectInput
                  placeholder="Search for a location..."
                  autoComplete="off"
                  value={locationSearch}
                  onChange={(event) => {
                    setLocationSearch(event.target.value);
                    setLocationOpen(true);
                  }}
                  onFocus={() => setLocationOpen(true)}
                />
                <CaretButton
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setLocationOpen((open) => !open);
                  }}
                  aria-label="Toggle location list"
                >
                  <ChevronDown />
                </CaretButton>
              </Select>
              {locationOpen ? (
                <DropdownPanel>
                  <DropdownScroll>
                    {filteredLocations.length === 0 &&
                    placePredictions.length === 0 ? (
                      <DropdownItem
                        css={{ color: '#979797', cursor: 'default' }}
                      >
                        No locations match this search.
                      </DropdownItem>
                    ) : (
                      <>
                        {filteredLocations.map((location) => (
                          <DropdownItem
                            key={location.id}
                            onClick={() => handleSelectLocation(location)}
                          >
                            {locationLabel(location)}
                          </DropdownItem>
                        ))}
                        {placePredictions.length > 0 ? (
                          <>
                            {filteredLocations.length > 0 ? (
                              <DropdownItem
                                css={{
                                  color: '#979797',
                                  cursor: 'default',
                                  fontSize: 12,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  padding: '8px 16px',
                                }}
                              >
                                Search results
                              </DropdownItem>
                            ) : null}
                            {placePredictions.map((prediction) => (
                              <DropdownItem
                                key={prediction.place_id}
                                onClick={() => handleSelectPlace(prediction)}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                  }}
                                >
                                  <span
                                    style={{
                                      color: '#1C1C1C',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {prediction.main_text}
                                  </span>
                                  {prediction.secondary_text ? (
                                    <span
                                      style={{ color: '#5B5B5B', fontSize: 12 }}
                                    >
                                      {prediction.secondary_text}
                                    </span>
                                  ) : null}
                                </div>
                              </DropdownItem>
                            ))}
                          </>
                        ) : null}
                      </>
                    )}
                  </DropdownScroll>
                  <DropdownFooter>
                    <AddRow
                      onClick={() => {
                        setLocationError(null);
                        setLocationModalDraft(null);
                        setLocationModalOpen(true);
                        setLocationOpen(false);
                      }}
                    >
                      <PlusGlyph>+</PlusGlyph>
                      Add location
                    </AddRow>
                  </DropdownFooter>
                </DropdownPanel>
              ) : null}
            </InputShell>
          </Field>

          {/* How to get there */}
          <Field gap="md">
            <FieldHeading>How to get there</FieldHeading>
            <TextArea
              placeholder="Tell your workers what to do or where to go to start their shift"
              value={howToGetThere}
              onChange={(event) =>
                update('address_instructions', event.target.value)
              }
            />
            <InstructionActions>
              <LinkButton
                type="button"
                onClick={() =>
                  onBrowseSaved(
                    'address_instructions',
                    'Browse saved directions',
                    (body) => update('address_instructions', body),
                  )
                }
              >
                Browse saved
              </LinkButton>
              {howToGetThereMatches ? (
                <LinkButton
                  type="button"
                  onClick={() => update('address_instructions', '')}
                >
                  Clear instructions
                </LinkButton>
              ) : (
                <SaveAffordance disabled={!howToGetThere.trim()}>
                  <SaveIcon
                    stroke={howToGetThere.trim() ? '#2E8722' : '#979797'}
                  />
                  <SaveButton
                    type="button"
                    disabled={!howToGetThere.trim()}
                    onClick={() =>
                      onSaveSnippet(
                        'address_instructions',
                        'Save directions',
                        howToGetThere,
                      )
                    }
                  >
                    Save
                  </SaveButton>
                </SaveAffordance>
              )}
            </InstructionActions>
          </Field>
        </FieldGroup>

        {/* Supervisor group */}
        <FieldGroup>
          <Field gap="lg">
            {verificationVisible ? (
              <Field gap="sm">
                <ToggleRow>
                  <ToggleLabel>Enable QR Code Check-in/Check-out</ToggleLabel>
                  <ToggleSwitch
                    type="button"
                    on={verificationEnabled}
                    onClick={() => setVerificationEnabled(!verificationEnabled)}
                    aria-pressed={verificationEnabled}
                  >
                    <ToggleKnob on={verificationEnabled} />
                  </ToggleSwitch>
                </ToggleRow>
                <FieldHint>
                  When enabled, workers will use QR code scanning to verify
                  their check-in and check-out times on-site, ensuring accurate
                  attendance tracking.
                </FieldHint>
              </Field>
            ) : null}
            <Field>
              <FieldHeading>Shift supervisor</FieldHeading>
              <InputShell ref={supervisorRef}>
                {supervisorOtherOpen && !selectedSupervisor ? (
                  <TriggerSelect onClick={() => setSupervisorOpen(true)}>
                    <span
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <PlusGlyph>+</PlusGlyph>
                      <span
                        style={{
                          fontSize: 14,
                          color: '#44A735',
                          fontFamily: "'Manrope', sans-serif",
                        }}
                      >
                        Enter shift supervisor details
                      </span>
                    </span>
                    <ChevronDown size={18} />
                  </TriggerSelect>
                ) : (
                  <>
                    <ReadonlyInput
                      type="text"
                      placeholder="Select your shift supervisor"
                      autoComplete="off"
                      readOnly
                      value={
                        selectedSupervisor
                          ? supervisorLabel(selectedSupervisor)
                          : ''
                      }
                      onClick={() => setSupervisorOpen(true)}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                      }}
                    >
                      <ChevronDown size={18} />
                    </span>
                  </>
                )}
                {supervisorOpen ? (
                  <DropdownPanel
                    css={{
                      top: 'calc(100% + 2px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                      zIndex: 200,
                    }}
                  >
                    <DropdownScroll>
                      {supervisors.length === 0 ? (
                        <DropdownItem
                          css={{ color: '#979797', cursor: 'default' }}
                        >
                          No supervisors yet for this billing account.
                        </DropdownItem>
                      ) : (
                        supervisors.map((supervisor) => (
                          <DropdownItem
                            key={supervisor.id}
                            onClick={() => handleSelectSupervisor(supervisor)}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                              }}
                            >
                              <span style={{ color: '#1C1C1C' }}>
                                {supervisorLabel(supervisor)}
                              </span>
                              <span style={{ color: '#979797', fontSize: 12 }}>
                                {supervisor.phone || 'No phone on file'}
                              </span>
                            </div>
                          </DropdownItem>
                        ))
                      )}
                    </DropdownScroll>
                    <DropdownFooter>
                      <AddRow
                        onClick={() => {
                          setSupervisorOtherOpen(true);
                          setSupervisorOpen(false);
                          update('contact_id', null);
                        }}
                      >
                        <PlusGlyph>+</PlusGlyph>
                        Enter shift supervisor details
                      </AddRow>
                    </DropdownFooter>
                  </DropdownPanel>
                ) : null}
              </InputShell>

              {supervisorOtherOpen && !selectedSupervisor ? (
                <SupervisorOtherFields>
                  <TextInput
                    type="text"
                    placeholder="Supervisor name"
                    value={supervisorName}
                    onChange={(event) => setSupervisorName(event.target.value)}
                    css={{ padding: '8px 12px' }}
                  />
                  <TextInput
                    type="text"
                    placeholder="Contact number"
                    value={supervisorPhone}
                    onChange={(event) => setSupervisorPhone(event.target.value)}
                    css={{ padding: '8px 12px' }}
                  />
                  {supervisorError ? (
                    <p style={{ color: '#C0392B', fontSize: 12, margin: 0 }}>
                      {supervisorError}
                    </p>
                  ) : null}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <PrimaryButton
                      type="button"
                      onClick={handleAddSupervisor}
                      disabled={savingSupervisor}
                    >
                      {savingSupervisor ? 'Saving…' : 'Add supervisor'}
                    </PrimaryButton>
                    <SecondaryButton
                      type="button"
                      onClick={() => {
                        setSupervisorOtherOpen(false);
                        setSupervisorError(null);
                        setSupervisorName('');
                        setSupervisorPhone('');
                      }}
                    >
                      Cancel
                    </SecondaryButton>
                  </div>
                </SupervisorOtherFields>
              ) : null}

              {showNoContactInput ? (
                <NoContactRow>
                  <NoContactDivider />
                  <TextInput type="tel" placeholder="Contact number" />
                </NoContactRow>
              ) : null}
            </Field>

            <ToggleRow>
              <ToggleLabel>
                Share supervisor&apos;s contact info with Gravyworkers 90
                minutes before the shift
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                on={shareContact}
                onClick={() => update('share_contact_number', !shareContact)}
                aria-pressed={shareContact}
              >
                <ToggleKnob on={shareContact} />
              </ToggleSwitch>
            </ToggleRow>
          </Field>

          {/* How to check-in */}
          <Field gap="md">
            <div>
              <FieldHeading>How to check-in</FieldHeading>
              <FieldHint>
                We&apos;ll show these to your workers 90 minutes before their
                shift
              </FieldHint>
            </div>
            <TextArea
              rows={5}
              placeholder="Tell your workers what to do or where to go to start their shift"
              value={checkIn}
              onChange={(event) =>
                update('contact_instructions', event.target.value)
              }
              css={{ padding: 16, resize: 'none', lineHeight: 1.4 }}
            />
            <InstructionActions>
              <LinkButton
                type="button"
                onClick={() =>
                  onBrowseSaved(
                    'check_in_instructions',
                    'Browse saved check-in instructions',
                    (body) => update('contact_instructions', body),
                  )
                }
              >
                Browse saved
              </LinkButton>
              {checkInMatches ? (
                <LinkButton
                  type="button"
                  onClick={() => update('contact_instructions', '')}
                >
                  Clear instructions
                </LinkButton>
              ) : (
                <SaveAffordance disabled={!checkIn.trim()}>
                  <SaveIcon stroke={checkIn.trim() ? '#2E8722' : '#979797'} />
                  <SaveButton
                    type="button"
                    disabled={!checkIn.trim()}
                    onClick={() =>
                      onSaveSnippet(
                        'check_in_instructions',
                        'Save check-in instructions',
                        checkIn,
                      )
                    }
                  >
                    Save
                  </SaveButton>
                </SaveAffordance>
              )}
            </InstructionActions>
          </Field>
        </FieldGroup>
      </DetailsCard>

      <AddLocationModal
        isOpen={locationModalOpen}
        saving={savingLocation}
        errorMessage={locationError}
        initialDraft={locationModalDraft}
        onCancel={() => {
          setLocationModalOpen(false);
          setLocationModalDraft(null);
        }}
        onAdd={handleSaveLocation}
        onBrowseSaved={() =>
          onBrowseSaved(
            'address_instructions',
            'Browse saved directions',
            (body) => update('address_instructions', body),
          )
        }
      />
    </PageContent>
  );
};

export default JobTypeStep;
